<script setup lang="ts">
import type { Group, GroupMember } from '~/models/Group'
import type { ContentFormWriter } from '~/models/Content/ContentFormWriter'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { faFileLines } from '@fortawesome/pro-regular-svg-icons'
import { KsIcon } from '@aschehoug/kloss-2'
import { KsDropdown, KsSpinner } from '@aschehoug/kloss'
import useGroupsStore from '~/stores/groups'
import { PendoFeature } from '~/models/Pendo'
import { GroupRole } from '~/models/Group'
import useAssignmentApi from '~/api/assignmentApi'

const { item } = defineProps<{ item: ContentFormWriter }>()

const activeStudent = defineModel<GroupMember | null>()

const { t } = useI18n()
const { loadGroupMembersById, sortMembers } = useGroupsStore()
const { sortedGroups, groupMembers } = storeToRefs(useGroupsStore())
const { getAssignmentResponse } = useAssignmentApi()

const activeGroup = ref<Group | null>(null)

const assignmentId = computed(() => `form_writer-${item.locationId}`)
const activeGroupId = computed(() => activeGroup.value?.groupId ?? '')

const students = computed(() => {
  if (!activeGroup.value?.groupId || !groupMembers.value?.[activeGroup.value.groupId]?.length)
    return []

  return groupMembers.value[activeGroup.value.groupId]
    .filter(member => member.groupRole === GroupRole.Member)
    .sort(sortMembers)
})

const studentsWithAnswers = computed(() =>
  students.value?.filter(student => hasAnswer(student.userId)))

const studentsWithoutAnswers = computed(() =>
  students.value?.filter(student => !hasAnswer(student.userId)))

const hasAnswer = (userId: string) => {
  const response = data.value?.find(({ student }) => student.userId === userId)
  return !!response?.answer && response.answer.length > 0
}

const { data, isLoading } = useQuery({
  queryKey: computed(() => ['form_writer_responses', assignmentId.value, activeGroupId.value]),
  queryFn: () => getAssignmentResponse(assignmentId.value, [activeGroupId.value]),
  enabled: computed(() => !!assignmentId.value && !!activeGroupId.value),
  staleTime: Infinity
})

watch(activeGroup, group =>
  group?.groupId ? loadGroupMembersById(group.groupId) : (activeStudent.value = null))
</script>

<template>
  <div class="sticky top-0 z-10 mx-auto flex flex-wrap items-center gap-2 rounded-xl border-2 border-[--theme-40] bg-[--theme-10] p-4 pt-9 xs:flex-nowrap print:hidden">
    <div class="absolute left-0 top-0 flex items-center gap-1.5 rounded-br-lg rounded-tl-lg bg-[--theme-40] px-2 py-1 text-sm font-medium text-[--theme-5]">
      <KsIcon :icon="faFileLines" />
      <p v-text="t('formWriter.delivered')" />
    </div>
    <label v-if="sortedGroups.length" class="flex flex-col gap-1">
      <span
        class="text-sm font-medium"
        v-text="t('formWriter.labels.group')"
      />
      <KsDropdown
        v-model="activeGroup"
        :options="sortedGroups"
        :placeholder="t('formWriter.labels.group')"
        :option-id="(group: Group) => group.groupId"
        :option-label="(group: Group) => group.name"
        :option-value="(group: Group) => group.groupId"
        :data-pendo="PendoFeature.FormWriter.SelectGroup"
      />
    </label>
    <KsSpinner
      v-if="isLoading"
      class="self-end"
      size="small"
    />
    <label v-if="activeGroup && !isLoading" class="flex flex-col gap-1">
      <span
        class="text-sm font-medium"
        v-text="t('formWriter.labels.visited', { number: studentsWithAnswers.length })"
      />
      <KsDropdown
        v-model="activeStudent"
        :options="studentsWithAnswers"
        :placeholder="t('formWriter.labels.chooseStudent')"
        :option-id="(student: GroupMember) => student.userId"
        :option-label="(student: GroupMember) => student.fullName"
        :option-value="(student: GroupMember) => student.userId"
        :data-pendo="PendoFeature.FormWriter.SelectStudent"
        :no-options="t('formWriter.labels.noStudents')"
      />
    </label>
    <label v-if="activeGroup && !isLoading" class="flex flex-col gap-1">
      <span
        class="text-sm font-medium"
        v-text="t('formWriter.labels.notVisited', { number: studentsWithoutAnswers.length })"
      />
      <KsDropdown
        :options="studentsWithoutAnswers"
        :placeholder="t('formWriter.labels.showStudents')"
        :option-id="(member: GroupMember) => member.userId"
        :option-label="(member: GroupMember) => member.fullName"
        :option-value="(member: GroupMember) => member.userId"
        :no-options="t('formWriter.labels.noStudents')"
        :selectable="() => false"
      />
    </label>
  </div>
</template>
